import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { injectSchema, getServiceSchema, getBreadcrumbSchema, getWebPageSchema } from '../../utils/seo';

export default function Services() {
  useEffect(() => {
    // Inject Schema.org structured data
    const schemas = [
      getWebPageSchema(
        'Nos Services - Spark\'Up',
        'Services professionnels de nettoyage de canapés, fauteuils, matelas et tissus à Lausanne'
      ),
      getServiceSchema(
        'Nettoyage professionnel de canapés et tissus',
        'Nettoyage en profondeur de tous types de tissus : velours, microfibre, lin, coton, cuir. Service à domicile à Lausanne et Canton de Vaud.'
      ),
      getBreadcrumbSchema([
        { name: 'Accueil', url: '/' },
        { name: 'Services', url: '/services' }
      ])
    ];
    
    injectSchema(schemas);
  }, []);

  const services = [
    {
      icon: "ri-sofa-line",
      title: "Nettoyage de canapé en tissu",
      description: "Nettoyage en profondeur de tous types de tissus : velours, microfibre, lin, coton. Élimination des taches, odeurs et acariens.",
      results: "Tissus rafraîchis, couleurs ravivées, élimination de 99% des bactéries",
      duration: "1h30 à 3h selon la taille",
      materials: ["Tissu", "Velours", "Microfibre", "Lin", "Coton"]
    },
    {
      icon: "ri-armchair-line",
      title: "Nettoyage de canapé en cuir",
      description: "Nettoyage délicat et nutrition du cuir. Traitement spécifique pour préserver la souplesse et l'éclat de votre cuir.",
      results: "Cuir nourri, protégé et brillant, souplesse restaurée",
      duration: "1h à 2h selon la taille",
      materials: ["Cuir lisse", "Cuir grainé", "Cuir nubuck", "Similicuir"]
    },
    {
      icon: "ri-armchair-fill",
      title: "Nettoyage de fauteuils et chaises",
      description: "Traitement adapté pour vos fauteuils et chaises rembourrés. Nettoyage minutieux de toutes les surfaces textiles.",
      results: "Tissus propres et désinfectés, aspect neuf retrouvé",
      duration: "30min à 1h par pièce",
      materials: ["Tous types de tissus", "Cuir", "Velours"]
    },
    {
      icon: "ri-hotel-bed-line",
      title: "Nettoyage de matelas",
      description: "Nettoyage hygiénique en profondeur. Élimination des taches, odeurs, acariens et allergènes pour un sommeil sain.",
      results: "Matelas désinfecté, anti-acariens, anti-odeurs, hygiène optimale",
      duration: "1h à 1h30 par matelas",
      materials: ["Matelas à ressorts", "Mousse", "Latex", "Mémoire de forme"]
    },
    {
      icon: "ri-layout-top-line",
      title: "Nettoyage de têtes de lit",
      description: "Nettoyage délicat des têtes de lit rembourrées. Traitement adapté selon le type de tissu ou de cuir.",
      results: "Tête de lit rafraîchie, taches éliminées, aspect neuf",
      duration: "30min à 1h",
      materials: ["Tissu", "Velours", "Cuir", "Similicuir"]
    },
    {
      icon: "ri-layout-bottom-line",
      title: "Nettoyage de tapis légers",
      description: "Nettoyage professionnel de vos tapis d'intérieur. Extraction en profondeur des saletés et ravivage des couleurs.",
      results: "Tapis propre, couleurs éclatantes, fibres revitalisées",
      duration: "30min à 1h30 selon la taille",
      materials: ["Laine", "Synthétique", "Coton", "Fibres naturelles"]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/39f01b0f1c683637cd8f53dc045bcf45/b7bb771b8d681dae1702525950b7e60d.png" 
                alt="Spark'Up Logo" 
                className="h-16 w-auto"
              />
            </Link>
            <div className="hidden md:flex space-x-10">
              <Link to="/" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                Accueil
              </Link>
              <Link to="/services" className="text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                Services
              </Link>
              <Link to="/processus" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                Processus
              </Link>
              <Link to="/a-propos" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                À propos
              </Link>
              <Link to="/contact" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                Contact
              </Link>
            </div>
            <Link to="/contact" className="bg-navy text-white px-8 py-3 rounded-full font-semibold hover:bg-navy-dark transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
              Devis gratuit
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-premium-gray via-white to-sky/5 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-sky font-semibold text-sm tracking-wider uppercase mb-4 block">Excellence</span>
          <h2 className="text-5xl lg:text-7xl font-bold text-navy mb-6">Nos Services</h2>
          <p className="text-xl lg:text-2xl text-gray-600 font-light max-w-3xl mx-auto">
            Des solutions professionnelles pour tous vos besoins de nettoyage textile
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-premium-gray rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all group">
                <div className="p-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky to-navy text-white rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                    <i className={`${service.icon} text-3xl`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-8 leading-relaxed font-light">
                    {service.description}
                  </p>
                  
                  <div className="space-y-6 mb-8">
                    <div className="flex items-start gap-4">
                      <i className="ri-checkbox-circle-fill text-sky text-2xl flex-shrink-0 mt-1"></i>
                      <div>
                        <p className="font-semibold text-navy mb-1">Résultats</p>
                        <p className="text-gray-600 text-sm font-light">{service.results}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <i className="ri-time-fill text-sky text-2xl flex-shrink-0 mt-1"></i>
                      <div>
                        <p className="font-semibold text-navy mb-1">Durée</p>
                        <p className="text-gray-600 text-sm font-light">{service.duration}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <i className="ri-price-tag-3-fill text-gold text-2xl flex-shrink-0 mt-1"></i>
                      <div>
                        <p className="font-semibold text-navy mb-1">Matériaux traités</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {service.materials.map((material, idx) => (
                            <span key={idx} className="bg-white px-3 py-1 rounded-full text-xs font-medium text-gray-700 shadow-sm">
                              {material}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Link to="/contact" className="block w-full bg-navy text-white text-center px-8 py-4 rounded-full font-semibold hover:bg-navy-dark transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
                    Obtenir un devis
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Professional Cleaning */}
      <section className="py-24 bg-premium-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="text-sky font-semibold text-sm tracking-wider uppercase mb-4 block">Avantages</span>
            <h3 className="text-4xl lg:text-5xl font-bold text-navy mb-6">
              Pourquoi un nettoyage professionnel ?
            </h3>
            <p className="text-xl text-gray-600 font-light">
              Les avantages d'un service expert
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-10 rounded-3xl shadow-md hover:shadow-xl transition-all text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky/20 to-navy/20 text-sky rounded-2xl mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold text-navy mb-3">Hygiène garantie</h4>
              <p className="text-gray-600 font-light">Élimination de 99% des bactéries, acariens et allergènes</p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-md hover:shadow-xl transition-all text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky/20 to-navy/20 text-sky rounded-2xl mx-auto mb-6">
                <i className="ri-time-line text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold text-navy mb-3">Gain de temps</h4>
              <p className="text-gray-600 font-light">Intervention rapide et efficace par des professionnels</p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-md hover:shadow-xl transition-all text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky/20 to-navy/20 text-sky rounded-2xl mx-auto mb-6">
                <i className="ri-star-line text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold text-navy mb-3">Résultats durables</h4>
              <p className="text-gray-600 font-light">Protection longue durée et préservation de vos meubles</p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-md hover:shadow-xl transition-all text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky/20 to-navy/20 text-sky rounded-2xl mx-auto mb-6">
                <i className="ri-leaf-line text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold text-navy mb-3">Produits adaptés</h4>
              <p className="text-gray-600 font-light">Solutions écologiques et respectueuses de vos tissus</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-navy to-navy-dark text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl lg:text-5xl font-bold mb-6">
            Prêt à redonner vie à vos meubles ?
          </h3>
          <p className="text-xl text-white/80 mb-10 font-light">
            Obtenez un devis gratuit et personnalisé en moins de 24h
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-navy px-10 py-4 rounded-full font-semibold text-lg hover:bg-premium-gray transition-all shadow-xl whitespace-nowrap cursor-pointer">
              Demander un devis gratuit
            </Link>
            <a href="tel:+41782036539" className="bg-sky text-white px-10 py-4 rounded-full font-semibold text-lg hover:bg-sky/90 transition-all shadow-xl whitespace-nowrap cursor-pointer">
              Appeler maintenant
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <img 
                src="https://static.readdy.ai/image/39f01b0f1c683637cd8f53dc045bcf45/b7bb771b8d681dae1702525950b7e60d.png" 
                alt="Spark'Up Logo" 
                className="h-12 w-auto mb-6"
              />
              <p className="text-white/70 mb-6 font-light leading-relaxed">
                Nettoyage professionnel de canapés et tissus à Lausanne
              </p>
              <div className="flex gap-4">
                <a href="https://share.google/v4Ro6xy2LeuVbtJiq" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-sky transition-all cursor-pointer">
                  <i className="ri-google-fill text-xl"></i>
                </a>
                <a href="https://www.instagram.com/sparkup.swiss/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-sky transition-all cursor-pointer">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6">Navigation</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Accueil</Link></li>
                <li><Link to="/services" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Services</Link></li>
                <li><Link to="/processus" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Processus</Link></li>
                <li><Link to="/a-propos" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">À propos</Link></li>
                <li><Link to="/contact" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6">Services</h4>
              <ul className="space-y-3">
                <li className="text-white/70 font-light">Nettoyage de canapés</li>
                <li className="text-white/70 font-light">Nettoyage de fauteuils</li>
                <li className="text-white/70 font-light">Nettoyage de matelas</li>
                <li className="text-white/70 font-light">Nettoyage de cuir</li>
                <li className="text-white/70 font-light">Nettoyage de tapis</li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6">Contact</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-sky text-xl flex-shrink-0"></i>
                  <span className="text-white/70 font-light">Lausanne, Canton de Vaud, Suisse</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-phone-line text-sky text-xl"></i>
                  <a href="tel:+41782036539" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">+41 78 203 65 39</a>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-mail-line text-sky text-xl"></i>
                  <a href="mailto:Sparkup.swiss@gmail.com" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Sparkup.swiss@gmail.com</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-white/60 font-light">
            <p>&copy; 2025 Spark'Up. Tous droits réservés. | <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
